// client/src/components/game_hud/InventoryPanel.jsx
// Итерация 7: сетка предметов с фильтром, поиском, tooltip.
//
// Источник данных — Redux (state.game.inventory).
// Пропсы: только onRefetch (для кнопки ↻), loading/error берём из Redux.

import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';

// ============================================================================
// КОНСТАНТЫ (иконки и метки для русских типов из ontology)
// ============================================================================

const TYPE_ICONS = {
  медицина: '💊',
  оружие: '⚔️',
  одежда: '👕',
  мебель: '🪑',
  посуда: '🍽️',
  инструменты: '🔧',
  управление: '🎛️',
  роботы: '🤖',
  транспорт: '🚗',
  еда: '🍖',
  напиток: '💧',
  расходник: '📦',
  материал: '📦',
  misc: '📋',
};

const TYPE_LABELS = {
  медицина: 'Медицина',
  оружие: 'Оружие',
  одежда: 'Одежда',
  мебель: 'Мебель',
  посуда: 'Посуда',
  инструменты: 'Инструменты',
  управление: 'Управление',
  роботы: 'Роботы',
  транспорт: 'Транспорт',
  еда: 'Еда',
  напиток: 'Напиток',
  расходник: 'Расходник',
  материал: 'Материал',
  misc: 'Прочее',
};

const ALL_FILTER = '__all__';

const EMPTY_INVENTORY = {
  items: [],
  capacity: 100,
  used: 0,
  credits: 0,
  weight_used: 0.0,
  volume_used: 0.0,
};

// ============================================================================
// КОМПОНЕНТ
// ============================================================================

export default function InventoryPanel({ onRefetch }) {
  const dispatch = useDispatch();

  // 🔥 Итерация 7: inventory, loading, error — из Redux
  const inventory = useSelector((state) => state.game?.inventory || EMPTY_INVENTORY);
  const loading = useSelector((state) => state.game?.inventoryLoading ?? false);
  const error = useSelector((state) => state.game?.inventoryError ?? null);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(ALL_FILTER);

  const items = inventory?.items || [];

  const availableTypes = useMemo(() => {
    const types = new Set(items.map((i) => i.type || 'misc'));
    return [ALL_FILTER, ...Array.from(types)];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter !== ALL_FILTER && (item.type || 'misc') !== typeFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const name = (item.name || '').toLowerCase();
        const itemId = (item.item_id || '').toLowerCase();
        if (!name.includes(q) && !itemId.includes(q)) return false;
      }
      return true;
    });
  }, [items, typeFilter, searchTerm]);

  const used = inventory?.used ?? items.length;
  const capacity = inventory?.capacity ?? 100;
  const credits = inventory?.credits ?? 0;
  const weightUsed = inventory?.weight_used ?? 0;
  const volumeUsed = inventory?.volume_used ?? 0;

  const usedColor =
    used > capacity * 0.9 ? 'error' : used > capacity * 0.7 ? 'warning' : 'default';

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок + статистика */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}
      >
        <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
          Инвентарь
        </Typography>

        <Chip
          label={`${used}/${capacity}`}
          size="small"
          color={usedColor}
          sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }}
        />

        {credits > 0 && (
          <Chip
            label={`💰 ${credits.toLocaleString()} кр`}
            size="small"
            variant="outlined"
            color="success"
            sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }}
          />
        )}

        {(weightUsed > 0 || volumeUsed > 0) && (
          <Chip
            label={`${weightUsed.toFixed(1)} кг · ${volumeUsed.toFixed(1)} л`}
            size="small"
            variant="outlined"
            sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.5rem' } }}
          />
        )}

        {onRefetch && (
          <Chip
            label="↻"
            size="small"
            variant="outlined"
            onClick={onRefetch}
            sx={{
              height: 18,
              minWidth: 18,
              cursor: 'pointer',
              '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 },
            }}
          />
        )}
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 1, py: 0.25, fontSize: '0.6rem' }}>
          {String(error)}
        </Alert>
      )}

      {/* Поиск + фильтр */}
      {items.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск по имени или item_id..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14 }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.65rem', height: 28 },
            }}
            sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.5 } }}
          />

          <Stack
            direction="row"
            spacing={0.25}
            sx={{ overflowX: 'auto', maxWidth: '50%', flexWrap: 'nowrap' }}
          >
            {availableTypes.slice(0, 5).map((t) => (
              <Chip
                key={t}
                label={
                  t === ALL_FILTER
                    ? 'Все'
                    : `${TYPE_ICONS[t] || '📋'} ${TYPE_LABELS[t] || t}`
                }
                size="small"
                onClick={() => setTypeFilter(t)}
                color={typeFilter === t ? 'primary' : 'default'}
                variant={typeFilter === t ? 'filled' : 'outlined'}
                sx={{
                  height: 22,
                  '& .MuiChip-label': { fontSize: '0.5rem' },
                  flexShrink: 0,
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* Сетка предметов */}
      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <InventoryIcon
            sx={{ fontSize: 28, color: 'text.secondary', opacity: 0.2, mb: 0.5 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem', display: 'block' }}
          >
            {items.length === 0 ? 'Инвентарь пуст' : 'Нет совпадений'}
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          spacing={0.5}
          sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
        >
          {filtered.map((item, i) => (
            <Grid item xs={6} sm={4} md={3} key={item.id || `item-${i}`}>
              <Tooltip
                arrow
                title={
                  <Box sx={{ maxWidth: 220 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, display: 'block', fontSize: '0.65rem' }}
                    >
                      {item.name || 'Предмет'}
                    </Typography>
                    {item.item_id && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.5rem',
                          display: 'block',
                          opacity: 0.8,
                          fontFamily: 'monospace',
                        }}
                      >
                        {item.item_id}
                      </Typography>
                    )}
                    {item.description && (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.55rem', display: 'block', mt: 0.25 }}
                      >
                        {item.description}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.5rem',
                        display: 'block',
                        mt: 0.25,
                        opacity: 0.8,
                      }}
                    >
                      Вес: {item.weight ?? 0} · Объём: {item.volume ?? 0}
                      {item.value > 0 && ` · Цена: ${item.value}`}
                    </Typography>
                  </Box>
                }
              >
                <Card
                  sx={{
                    borderRadius: 1,
                    elevation: 0,
                    border: '1px solid',
                    borderColor: item.equipped === true ? 'success.main' : 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    position: 'relative',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
                    <Stack direction="row" spacing={0.5} alignItems="flex-start">
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
                        {TYPE_ICONS[item.type] || '📋'}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            display: 'block',
                          }}
                          noWrap
                        >
                          {item.name || 'Предмет'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.5rem', display: 'block' }}
                        >
                          x{item.quantity || 1}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>

                  {item.equipped === true && (
                    <Chip
                      label="✓"
                      size="small"
                      color="success"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        height: 12,
                        minWidth: 12,
                        '& .MuiChip-label': {
                          fontSize: '0.4rem',
                          px: 0.25,
                          lineHeight: 1,
                        },
                      }}
                    />
                  )}
                </Card>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
